import { useCallback, useEffect, useRef, useState } from "react";
import { io, Socket } from "socket.io-client";
import {
  localAuthRepository,
  staffSessionRevisionKey,
} from "../repository/localAuthRepository";
import { refreshStaffAccessToken } from "../infrastructure/httpDao";
import {
  type ChatMessage,
  type ChatMessageReaction,
  type ChatRoomActivity,
  type ChatRoomReadEvent,
  type ChatRoomWorkflowActivity,
} from "../types/chat";
import { getPublicSocketBaseUrl } from "@/lib/apiBaseUrl";

type ChatSocketError = {
  code: string;
  roomId?: string;
  message: string;
};

type JoinedRoomEvent = {
  roomId: string;
};

export type StaffInboxChangedEvent = {
  reason: "event-recorded" | "read-state-changed" | "preferences-changed";
  occurredAt: string;
};

const toBearerToken = (token: string) => `Bearer ${token}`;

const emitJoinRoom = (socket: Socket, roomId: string) => {
  socket.emit("joinRoom", { roomId });
};

// ヘッダー・一覧・会話画面で同じ接続を共有する。最後の利用者が離れた時だけ破棄。
type ChatConnection = {
  socket: Socket;
  revision: string;
  handshakeToken: string;
  listeners: Set<(connected: boolean) => void>;
  roomOwners: Map<string, Set<symbol>>;
  roomAccessRecoveryAttempts: Set<string>;
  authRecovery: Promise<void> | null;
  authFailureRecoveryAttempted: boolean;
  isConnected: boolean;
  readyRevision: number;
  readyListeners: Set<(revision: number) => void>;
  dispose: () => void;
};

let sharedConnection: ChatConnection | null = null;

const isCurrentSession = (connection: ChatConnection) =>
  sharedConnection === connection &&
  connection.revision === localAuthRepository.getStaffSessionRevision() &&
  !!localAuthRepository.getToken();

const recoverAuthentication = (connection: ChatConnection) => {
  if (!isCurrentSession(connection)) return;
  if (connection.authRecovery) return connection.authRecovery;
  const recovery = (async () => {
    try {
      const latestToken = localAuthRepository.getToken();
      const token =
        latestToken && latestToken !== connection.handshakeToken
          ? latestToken
          : await refreshStaffAccessToken();
      // ログアウト・別アカウントへのログイン・unmount後は古い接続を復活させない。
      if (!isCurrentSession(connection)) return;
      connection.handshakeToken = token;
      connection.socket.auth = { token: toBearerToken(token) };
      connection.socket.disconnect();
      connection.socket.connect();
    } catch {
      console.error("Socket authentication recovery failed");
    }
  })().finally(() => {
    if (connection.authRecovery === recovery) connection.authRecovery = null;
  });
  connection.authRecovery = recovery;
  return recovery;
};

const createConnection = (token: string): ChatConnection => {
  const socket = io(getPublicSocketBaseUrl(), {
    transports: ["websocket"],
    forceNew: true,
    reconnection: true,
    reconnectionAttempts: 8,
    reconnectionDelay: 1_000,
    reconnectionDelayMax: 10_000,
    randomizationFactor: 0.5,
    timeout: 20_000,
    auth: { token: toBearerToken(token) },
  });
  const connection: ChatConnection = {
    socket,
    revision: localAuthRepository.getStaffSessionRevision(),
    handshakeToken: token,
    listeners: new Set(),
    roomOwners: new Map(),
    roomAccessRecoveryAttempts: new Set(),
    authRecovery: null,
    authFailureRecoveryAttempted: false,
    isConnected: false,
    readyRevision: 0,
    readyListeners: new Set(),
    dispose: () => {},
  };
  const notifyConnection = (connected: boolean) => {
    connection.isConnected = connected;
    connection.listeners.forEach((listener) => listener(connected));
  };
  socket.on("connect", () => {
    if (!isCurrentSession(connection)) {
      socket.disconnect();
      return;
    }
    // transport接続の通知は旧APIでも維持する。
    notifyConnection(true);
    connection.roomOwners.forEach((_, roomId) => emitJoinRoom(socket, roomId));
  });
  socket.on("chatReady", () => {
    if (!isCurrentSession(connection)) return;
    connection.authFailureRecoveryAttempted = false;
    notifyConnection(true);
    connection.readyRevision += 1;
    connection.readyListeners.forEach((listener) =>
      listener(connection.readyRevision)
    );
  });
  socket.on("disconnect", () => notifyConnection(false));
  socket.on("connect_error", () => notifyConnection(false));
  socket.on("chatError", (data: ChatSocketError | null) => {
    if (data?.code === "AUTH_FAILED") {
      // 無効化された利用者等で、手動connectによる無制限の再認証ループを作らない。
      if (connection.authFailureRecoveryAttempted) return;
      connection.authFailureRecoveryAttempted = true;
      void recoverAuthentication(connection);
    } else if (
      data?.code === "ROOM_ACCESS_DENIED" &&
      data.roomId &&
      !connection.roomAccessRecoveryAttempts.has(data.roomId)
    ) {
      connection.roomAccessRecoveryAttempts.add(data.roomId);
      void recoverAuthentication(connection);
    }
  });
  socket.on("newMessage", (message: ChatMessage | null) => {
    if (message?.chatRoomId) {
      connection.authFailureRecoveryAttempted = false;
      connection.roomAccessRecoveryAttempts.delete(message.chatRoomId);
    }
  });
  socket.on("joinedRoom", (data: JoinedRoomEvent | null) => {
    if (data?.roomId) {
      connection.authFailureRecoveryAttempted = false;
      connection.roomAccessRecoveryAttempts.delete(data.roomId);
    }
  });
  const updateHandshake = () => {
    if (!isCurrentSession(connection)) {
      socket.disconnect();
      return;
    }
    const latestToken = localAuthRepository.getToken()!;
    connection.handshakeToken = latestToken;
    socket.auth = { token: toBearerToken(latestToken) };
  };
  socket.io.on("reconnect_attempt", updateHandshake);
  const resume = () => {
    if (document.visibilityState !== "visible") return;
    const tokenChanged =
      localAuthRepository.getToken() !== connection.handshakeToken;
    updateHandshake();
    // 自動再試行の上限を超えていても、復帰・回線復旧という明示的な契機で再接続する。
    if (
      isCurrentSession(connection) &&
      (!socket.connected || tokenChanged) &&
      !connection.authRecovery
    ) {
      // 接続中にauthを書き換えるだけではサーバーのhandshakeは更新されない。
      if (socket.connected) socket.disconnect();
      socket.connect();
    }
  };
  const handleSessionChange = (event: StorageEvent) => {
    if (event.key === staffSessionRevisionKey && !isCurrentSession(connection))
      socket.disconnect();
  };
  window.addEventListener("online", resume);
  window.addEventListener("focus", resume);
  window.addEventListener("storage", handleSessionChange);
  document.addEventListener("visibilitychange", resume);
  let disposed = false;
  connection.dispose = () => {
    if (disposed) return;
    disposed = true;
    notifyConnection(false);
    window.removeEventListener("online", resume);
    window.removeEventListener("focus", resume);
    window.removeEventListener("storage", handleSessionChange);
    document.removeEventListener("visibilitychange", resume);
    socket.io.off("reconnect_attempt", updateHandshake);
    socket.removeAllListeners();
    socket.disconnect();
  };
  return connection;
};

export const useChatSocket = () => {
  const socketRef = useRef<Socket | null>(null);
  const joinedRoomIdsRef = useRef(new Set<string>());
  const connectionRef = useRef<ChatConnection | null>(null);
  const ownerRef = useRef(Symbol("chat-consumer"));
  const subscriptionsRef = useRef(new Set<() => void>());
  const [isConnected, setIsConnected] = useState(false);
  const [readyRevision, setReadyRevision] = useState(0);

  useEffect(() => {
    const token = localAuthRepository.getToken();
    if (!token) return;
    if (sharedConnection && !isCurrentSession(sharedConnection)) {
      sharedConnection.dispose();
      sharedConnection = null;
    }
    const connection = sharedConnection ?? createConnection(token);
    sharedConnection = connection;
    connectionRef.current = connection;
    socketRef.current = connection.socket;
    connection.listeners.add(setIsConnected);
    connection.readyListeners.add(setReadyRevision);
    setIsConnected(connection.isConnected);
    setReadyRevision(connection.readyRevision);

    return () => {
      subscriptionsRef.current.forEach((unsubscribe) => unsubscribe());
      for (const roomId of joinedRoomIdsRef.current) {
        const owners = connection.roomOwners.get(roomId);
        owners?.delete(ownerRef.current);
        if (owners?.size === 0) {
          connection.roomOwners.delete(roomId);
          connection.roomAccessRecoveryAttempts.delete(roomId);
          if (connection.socket.connected)
            connection.socket.emit("leaveRoom", { roomId });
        }
      }
      joinedRoomIdsRef.current.clear();
      connection.listeners.delete(setIsConnected);
      connection.readyListeners.delete(setReadyRevision);
      socketRef.current = null;
      connectionRef.current = null;
      if (connection.listeners.size === 0) {
        if (sharedConnection === connection) sharedConnection = null;
        connection.dispose();
      }
    };
  }, []);

  const joinRoom = useCallback((roomId: string, _userId?: string) => {
    const connection = connectionRef.current;
    if (!connection || joinedRoomIdsRef.current.has(roomId)) return;
    joinedRoomIdsRef.current.add(roomId);
    const owners = connection.roomOwners.get(roomId) ?? new Set<symbol>();
    const needsJoin = owners.size === 0;
    owners.add(ownerRef.current);
    connection.roomOwners.set(roomId, owners);
    if (needsJoin && connection.socket.connected)
      emitJoinRoom(connection.socket, roomId);
  }, []);

  const leaveRoom = useCallback((roomId: string) => {
    joinedRoomIdsRef.current.delete(roomId);
    const connection = connectionRef.current;
    const owners = connection?.roomOwners.get(roomId);
    owners?.delete(ownerRef.current);
    if (connection && owners?.size === 0) {
      connection.roomOwners.delete(roomId);
      connection.roomAccessRecoveryAttempts.delete(roomId);
      connection.socket.emit("leaveRoom", { roomId });
    }
  }, []);

  const sendMessage = useCallback(
    (roomId: string, content: string, senderId: string) => {
      socketRef.current?.emit("sendMessage", {
        roomId,
        content,
        senderId,
      });
    },
    []
  );

  const subscribe = useCallback(
    <Payload>(event: string, callback: (payload: Payload) => void) => {
      const socket = socketRef.current;
      socket?.on(event, callback);
      let active = true;
      const unsubscribe = () => {
        if (!active) return;
        active = false;
        socket?.off(event, callback);
        subscriptionsRef.current.delete(unsubscribe);
      };
      subscriptionsRef.current.add(unsubscribe);
      return unsubscribe;
    },
    []
  );

  const onNewMessage = useCallback(
    (callback: (message: ChatMessage) => void) => {
      return subscribe("newMessage", callback);
    },
    [subscribe]
  );

  const onReactionAdded = useCallback(
    (callback: (reaction: ChatMessageReaction) => void) => {
      return subscribe("reactionAdded", callback);
    },
    [subscribe]
  );

  const onReactionRemoved = useCallback(
    (callback: (reaction: ChatMessageReaction) => void) => {
      return subscribe("reactionRemoved", callback);
    },
    [subscribe]
  );

  const onRoomActivity = useCallback(
    (callback: (activity: ChatRoomActivity) => void) => {
      return subscribe("roomActivity", callback);
    },
    [subscribe]
  );

  const onRoomWorkflowChanged = useCallback(
    (callback: (activity: ChatRoomWorkflowActivity) => void) => {
      return subscribe("roomWorkflowChanged", callback);
    },
    [subscribe]
  );

  const onRoomRead = useCallback(
    (callback: (event: ChatRoomReadEvent) => void) => {
      return subscribe("roomRead", callback);
    },
    [subscribe]
  );

  const onChatError = useCallback(
    (callback: (error: ChatSocketError) => void) => {
      return subscribe("chatError", callback);
    },
    [subscribe]
  );

  const onStaffInboxChanged = useCallback(
    (callback: (event: StaffInboxChangedEvent) => void) => {
      return subscribe("staffInboxChanged", callback);
    },
    [subscribe]
  );

  return {
    socket: socketRef.current,
    isConnected,
    readyRevision,
    joinRoom,
    leaveRoom,
    sendMessage,
    onNewMessage,
    onReactionAdded,
    onReactionRemoved,
    onRoomActivity,
    onRoomWorkflowChanged,
    onRoomRead,
    onChatError,
    onStaffInboxChanged,
  };
};
